import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {
  login as authenticate,
  getCurrentUser,
  logout as apiLogout,
  refreshSession as apiRefreshSession,
  ssoLogin,
} from '../api/authService';
import { getOrganizations, getOrganization } from '../api/orgService';
import { Box, CircularProgress } from '@mui/material';
import { toast } from 'react-hot-toast';
import { Organization } from '../types/organization';
import { EnrichedUser, AuthContextType, AuthFlowUiOptions } from '../types/auth';
// import { updateUserOrgId } from "../api/userService";
import { IdleLogout } from '../components/idleLogout';
import {
  setDatadogUser,
  clearDatadogUser,
  updateDatadogView,
  captureHandledError,
} from '../utilities/datadog';
const IDLE_TIMEOUT_MINUTES = 30;
/** Idle window while migration mapping UI is open (effectively no workday timeout). */
const MAPPING_IDLE_TIMEOUT_MINUTES = 8 * 60;
const WARNING_BEFORE_MS = 30 * 1000; // Show warning 30s before expiry
const IDLE_KEY = 'msapp_idle_expiry';
// Keep backend JWT alive while the user is active (must be < IDLE_TIMEOUT_MINUTES).
const SESSION_REFRESH_THROTTLE_MS = 5 * 60 * 1000;

const setInitialIdleSession = () => {
  const now = Date.now();
  const expiresAt = new Date(
    now + IDLE_TIMEOUT_MINUTES * 60 * 1000
  ).toISOString();
  const payload = {
    id: now, // session start time
    time: expiresAt, // next expiration time
  };
  localStorage.setItem(IDLE_KEY, JSON.stringify(payload));
};

const updateIdleExpiryTime = (timeoutMinutes: number = IDLE_TIMEOUT_MINUTES) => {
  const item = localStorage.getItem(IDLE_KEY);
  if (!item) return;
  try {
    const parsed = JSON.parse(item);
    parsed.time = new Date(
      Date.now() + timeoutMinutes * 60 * 1000
    ).toISOString();
    localStorage.setItem(IDLE_KEY, JSON.stringify(parsed));
  } catch {
    setInitialIdleSession(); // fallback if malformed
  }
};

const getIdleExpiryLS = (): number | null => {
  const item = localStorage.getItem(IDLE_KEY);
  if (!item) return null;
  try {
    const parsed = JSON.parse(item);
    return new Date(parsed.time).getTime();
  } catch {
    return null;
  }
};

const clearIdleExpiryLS = () => {
  localStorage.removeItem(IDLE_KEY);
};

/** Wire or already-normalized user payloads consumed by AuthContext. */
type UserApiRow = {
  id: string;
  first_name?: string;
  firstName?: string;
  last_name?: string;
  lastName?: string;
  email?: string;
  phone_number?: string;
  phoneNumber?: string;
  image_id?: string | null;
  imageId?: string | null;
  image_url?: string | null;
  imageUrl?: string | null;
  organization_role?: string;
  organizationRole?: string;
  is_sso_user?: boolean;
  isSsoUser?: boolean;
  last_login?: string | null;
  lastLogin?: string | null;
  failed_login_attempts?: number | null;
  failedLoginAttempts?: number | null;
  org_id?: string | null;
  email_signature?: string | null;
};

// Helper function to get a fingerprint of the organization list, reducing flicker on reload
const getOrgListFingerprint = (orgs: Organization[]): string => {
  const signature = orgs
    .map(org =>
      [
        org.id,
        org.name,
        org.icon_url ?? '',
        org.logo_url ?? '',
        [...(org.aliases ?? [])].sort().join(','),
        org.sector ?? '',
        org.domain ?? '',
        org.csm?.id ?? org.csm_id ?? '',
      ].join('|')
    )
    .join('||');

  // Fast deterministic hash (FNV-1a 32-bit) for lightweight change checks.
  let hash = 0x811c9dc5;
  for (let i = 0; i < signature.length; i += 1) {
    hash ^= signature.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }

  return (hash >>> 0).toString(16).padStart(8, '0');
};

const formatUser = (user: UserApiRow): EnrichedUser => ({
  id: user.id,
  firstName: user.first_name ?? user.firstName ?? '',
  lastName: user.last_name ?? user.lastName ?? '',
  email: user.email ?? '',
  phoneNumber: user.phone_number ?? user.phoneNumber,
  imageId: user.image_id ?? user.imageId ?? null,
  imageUrl: user.image_url ?? user.imageUrl ?? null,
  organizationRole: user.organization_role ?? user.organizationRole ?? '',
  isSsoUser: Boolean(user.is_sso_user ?? user.isSsoUser),
  lastLogin: user.last_login ?? user.lastLogin ?? undefined,
  failedLoginAttempts:
    user.failed_login_attempts ?? user.failedLoginAttempts ?? undefined,
  org_id: user.org_id || undefined,
  email_signature: user.email_signature ?? undefined,
});

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<EnrichedUser | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [userOrgs, setUserOrgs] = useState<Organization[]>([]);
  const userOrgsFingerprintRef = useRef<string>('');
  const [activePage, setActivePageState] = useState<string>('login');
  const [isShowingWarning, setIsShowingWarning] = useState(false);
  const [mappingSessionKeepAlive, setMappingSessionKeepAliveState] =
    useState(false);
  const lastSessionRefreshAtRef = useRef(0);

  const slideBackendSession = useCallback(async (force = false) => {
    const now = Date.now();
    if (
      !force &&
      now - lastSessionRefreshAtRef.current < SESSION_REFRESH_THROTTLE_MS
    ) {
      return;
    }
    lastSessionRefreshAtRef.current = now;
    await apiRefreshSession();
  }, []);

  const extendIdleSession = useCallback(
    (forceRefresh = false) => {
      updateIdleExpiryTime(
        mappingSessionKeepAlive
          ? MAPPING_IDLE_TIMEOUT_MINUTES
          : IDLE_TIMEOUT_MINUTES
      );
      void slideBackendSession(forceRefresh);
    },
    [slideBackendSession, mappingSessionKeepAlive]
  );

  const setMappingSessionKeepAlive = useCallback(
    (enabled: boolean) => {
      setMappingSessionKeepAliveState(enabled);
      if (enabled) {
        setIsShowingWarning(false);
        updateIdleExpiryTime(MAPPING_IDLE_TIMEOUT_MINUTES);
        void slideBackendSession(true);
      } else {
        updateIdleExpiryTime(IDLE_TIMEOUT_MINUTES);
      }
    },
    [slideBackendSession]
  );

  // While mapping keep-alive is on, refresh JWT on an interval even without activity.
  useEffect(() => {
    if (!isLoggedIn || !mappingSessionKeepAlive) return undefined;
    const interval = setInterval(() => {
      updateIdleExpiryTime(MAPPING_IDLE_TIMEOUT_MINUTES);
      void slideBackendSession(true);
    }, SESSION_REFRESH_THROTTLE_MS);
    return () => clearInterval(interval);
  }, [isLoggedIn, mappingSessionKeepAlive, slideBackendSession]);

  const requestPageChange = useCallback(
    (nextPage: string): boolean => {
      if (!nextPage || nextPage === activePage) {
        return true;
      }

      const navigationEvent = new CustomEvent('msapp:before-page-change', {
        detail: { from: activePage, to: nextPage },
        cancelable: true,
      });

      window.dispatchEvent(navigationEvent);
      if (navigationEvent.defaultPrevented) {
        return false;
      }

      setActivePageState(nextPage);
      return true;
    },
    [activePage]
  );

  const fetchAndSetOrganizations = async (): Promise<Organization[]> => {
    try {
      const orgs = await getOrganizations();
      if (orgs && orgs.length > 0) {
        const nextFingerprint = getOrgListFingerprint(orgs);
        if (userOrgsFingerprintRef.current !== nextFingerprint) {
          userOrgsFingerprintRef.current = nextFingerprint;
          setUserOrgs(orgs);
        }
        return orgs;
      } else {
        toast.error('No organizations found.');
        userOrgsFingerprintRef.current = '';
        setUserOrgs([]);
        return [];
      }
    } catch (error) {
      captureHandledError('Error fetching organizations', error);
      toast.error('Failed to fetch organizations. Please try again later.');
      userOrgsFingerprintRef.current = '';
      setUserOrgs([]);
      return [];
    }
  };

  const initializeApp = useCallback(async () => {
    setIsLoading(true);
    try {
      const fetchedUser = await getCurrentUser();
      if (fetchedUser) {
        // mark that we had a logged-in session so future 401s will trigger handleUnauth
        try {
          window.__IS_MSAPP_LOGGED_IN__ = true;
        } catch {
          void 0;
        }
        const formattedUser = formatUser(fetchedUser);
        setUser(formattedUser);
        // Set Datadog user context
        setDatadogUser(
          formattedUser.id.toString(),
          `${formattedUser.firstName} ${formattedUser.lastName}`.trim(),
          formattedUser.email
        );

        // Fetch and process organizations
        const orgs = await fetchAndSetOrganizations();
        if (orgs.length > 0) {
          // Get selectedOrgId from localStorage
          const selectedOrgId = localStorage.getItem('selectedOrgId');
          // selectedOrgId is now a string
          const selectedOrg = orgs.find(org => org.id === selectedOrgId);

          if (selectedOrg) {
            setOrganization(selectedOrg); // Set to the saved organization
            const updatedUser = { ...fetchedUser, org_id: selectedOrgId };

            const formattedUpdatedUser = formatUser(updatedUser);
            setUser(formattedUpdatedUser);
            // Update Datadog user context with org info
            setDatadogUser(
              formattedUpdatedUser.id.toString(),
              `${formattedUpdatedUser.firstName} ${formattedUpdatedUser.lastName}`.trim(),
              formattedUpdatedUser.email
            );
          } else {
            const fetchedUserOrg = orgs.find(
              org => org.id === fetchedUser.org_id
            );
            setOrganization(fetchedUserOrg || orgs[0]); // Default to the first organization
            localStorage.setItem(
              'selectedOrgId',
              (fetchedUserOrg || orgs[0]).id.toString()
            );
            const updatedUser = {
              ...fetchedUser,
              org_id: (fetchedUserOrg || orgs[0]).id,
            };

            const formattedUpdatedUser = formatUser(updatedUser);
            setUser(formattedUpdatedUser);
            // Update Datadog user context with org info
            setDatadogUser(
              formattedUpdatedUser.id.toString(),
              `${formattedUpdatedUser.firstName} ${formattedUpdatedUser.lastName}`.trim(),
              formattedUpdatedUser.email
            );
          }
        }

        setIsLoggedIn(true);
        // Track page with history api
        // Preserve no deep linking policy
        const initialPage = 'dashboard';
        setActivePageState(initialPage);
        window.history.replaceState(
          { page: initialPage, idx: 0 },
          '',
          window.location.href
        );
      } else {
        resetAuthState();
      }
    } catch (error) {
      captureHandledError('Error initializing app', error);
      resetAuthState();
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    initializeApp();
  }, [initializeApp]);

  const resetAuthState = () => {
    setIsLoggedIn(false);
    setUser(null);
    userOrgsFingerprintRef.current = '';
    setUserOrgs([]);
    setOrganization(null);
    setActivePageState('login');
    // Clear Datadog user context
    clearDatadogUser();
    // Keep History API state in sync on logout without changing URL
    window.history.replaceState(
      { page: 'login', idx: 0 },
      '',
      window.location.href
    );
    // clear global logged-in flag so initial 401s won't dispatch
    try {
      delete window.__IS_MSAPP_LOGGED_IN__;
    } catch {
      // delete on window can fail in exotic environments; ignore.
      void 0;
    }
  };

  // Global listener for API 401s -> redirect to login (SPA: reset context and show login)
  useEffect(() => {
    const handleUnauth = () => {
      // Clear any client-side session bits if desired
      try {
        localStorage.removeItem('selectedOrgId');
        localStorage.removeItem(IDLE_KEY);
      } catch {
        // localStorage can throw when disabled or in private browsing.
        void 0;
      }
      resetAuthState();
    };

    window.addEventListener(
      'msapp:unauthenticated',
      handleUnauth as EventListener
    );
    return () =>
      window.removeEventListener(
        'msapp:unauthenticated',
        handleUnauth as EventListener
      );
  }, []);

  const changeOrganization = async (orgId: string) => {
    try {
      const org = await getOrganization(orgId);
      const fetchedUser_ = await getCurrentUser();
      if (org && fetchedUser_) {
        // update the formattedUser with the new orgId
        const updatedUser = { ...fetchedUser_, org_id: org.id };

        setUser(formatUser(updatedUser));
        setOrganization(org);
        localStorage.setItem('selectedOrgId', org.id.toString());
        toast.success(`Organization changed to ${org.name}`);
        // After changing org, route user back to dashboard to avoid stale/nonexistent page data
        setActivePageState('dashboard');
      } else {
        toast.error('Failed to update organization. Organization not found.');
      }
    } catch (error) {
      captureHandledError('Error changing organization', error, { orgId });
      toast.error('Failed to update organization.');
    }
  };

  const updateUserInContext = (updatedUser: EnrichedUser) => {
    const formattedUser = formatUser(updatedUser);
    setUser(prevUser => ({
      ...prevUser, // Preserve existing fields
      ...formattedUser, // Overwrite with updated fields
    }));
  };

  const login = async (
    userOrEmail: string,
    password: string,
    options?: AuthFlowUiOptions
  ) => {
    try {
      const loginSuccess = await authenticate(userOrEmail, password);
      if (loginSuccess) {
        const fetchedUser = await getCurrentUser();

        if (fetchedUser) {
          // successful interactive login: mark logged-in session
          try {
            window.__IS_MSAPP_LOGGED_IN__ = true;
          } catch {
            void 0;
          }
          const formattedUser = formatUser(fetchedUser);
          setUser(formattedUser);
          // Set Datadog user context
          setDatadogUser(
            formattedUser.id.toString(),
            `${formattedUser.firstName} ${formattedUser.lastName}`.trim(),
            formattedUser.email
          );

          // Fetch organizations after login
          const orgs = await fetchAndSetOrganizations();
          if (orgs.length > 0) {
            // Check localStorage for selected organization
            const selectedOrgId = localStorage.getItem('selectedOrgId');
            const selectedOrg = orgs.find(org => org.id === selectedOrgId);

            if (selectedOrg) {
              setOrganization(selectedOrg); // Set to the saved organization
              const updatedUser = { ...fetchedUser, org_id: selectedOrgId };
              setUser(formatUser(updatedUser));
            } else {
              const fetchedUserOrg = orgs.find(
                org => org.id === fetchedUser.org_id
              );
              setOrganization(fetchedUserOrg || orgs[0]); // Default to the first organization
              localStorage.setItem(
                'selectedOrgId',
                (fetchedUserOrg || orgs[0]).id.toString()
              );
              const updatedUser = {
                ...fetchedUser,
                org_id: (fetchedUserOrg || orgs[0]).id,
              };
              const formattedUpdatedUser = formatUser(updatedUser);
              setUser(formattedUpdatedUser);
              // Update Datadog user context with org info
              setDatadogUser(
                formattedUpdatedUser.id.toString(),
                `${formattedUpdatedUser.firstName} ${formattedUpdatedUser.lastName}`.trim(),
                formattedUpdatedUser.email
              );
            }
          }

          setIsLoggedIn(true);
          setActivePageState('dashboard');
          // Push History API state for proper back/forward support without changing URL
          window.history.pushState(
            { page: 'dashboard' },
            '',
            window.location.href
          );
          setInitialIdleSession();
        } else {
          toast.error('Login failed: Could not retrieve user data.');
          resetAuthState();
        }
      } else {
        toast.error('Authentication failed. Please try again.');
        resetAuthState();
      }
    } catch (error) {
      captureHandledError('Login error', error);
      const message =
        error instanceof Error ? error.message : 'Incorrect email or password.';

      if (
        !options?.shouldSuppressErrorToast &&
        !message.toLowerCase().includes('please use sso')
      ) {
        toast.error(message);
      }
      resetAuthState();
      throw error;
    }
  };

  const logout = useCallback(() => {
    // Close idle dialog first so its countdown cannot call logout again.
    setIsShowingWarning(false);
    apiLogout();
    resetAuthState();
    clearIdleExpiryLS();
    try {
      // Match 401 handler: drop stale org so post-logout remounts cannot refetch it.
      localStorage.removeItem('selectedOrgId');
    } catch {
      void 0;
    }
    try {
      delete window.__IS_MSAPP_LOGGED_IN__;
    } catch {
      void 0;
    }
    toast.success('You have been logged out.', {
      id: 'logout',
      duration: 7000,
    });
  }, []);

  const initiateSSOLogin = async (
    email: string,
    options?: AuthFlowUiOptions
  ) => {
    try {
      await ssoLogin(email);
    } catch (error: unknown) {
      if (options?.shouldSuppressErrorToast) {
        // Expected probe outcomes (e.g. 404 no config) - caller handles UX.
        throw error;
      }
      captureHandledError('SSO login error', error);
      const message =
        error instanceof Error ? error.message : String(error);
      toast.error('SSO login error: ' + message, {
        style: { backgroundColor: '#f44336', color: '#ffffff' },
        iconTheme: { primary: '#ffffff', secondary: '#f44336' },
        position: 'top-center',
      });
    }
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    extendIdleSession(true);
  }, [isLoggedIn, extendIdleSession]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const resetIdleTimer = () => {
      if (!isShowingWarning) {
        extendIdleSession(false);
      }
    };

    const checkIdle = () => {
      if (mappingSessionKeepAlive) return;
      const expiry = getIdleExpiryLS();
      const now = Date.now();

      if (!expiry) return;

      const timeLeft = expiry - now;

      if (timeLeft <= 0) {
        clearIdleExpiryLS();
        setIsShowingWarning(false);
        logout(); // auto-logout
      } else if (timeLeft <= WARNING_BEFORE_MS && !isShowingWarning) {
        setIsShowingWarning(true); // show warning dialog
      }
    };

    const events = ['mousemove', 'keydown', 'scroll'];
    events.forEach(event => window.addEventListener(event, resetIdleTimer));

    const interval = setInterval(checkIdle, 5000); // check every 5s

    return () => {
      events.forEach(event =>
        window.removeEventListener(event, resetIdleTimer)
      );
      clearInterval(interval);
    };
  }, [
    isLoggedIn,
    logout,
    isShowingWarning,
    extendIdleSession,
    mappingSessionKeepAlive,
  ]);

  // Push History API state entries when activePage changes (no visible URL changes)
  // Also update Datadog RUM view name when page changes
  useEffect(() => {
    if (!activePage) return;
    // Avoid pushing when state already matches
    const currentState = window.history.state as { page?: string } | null;
    if (!currentState || currentState.page !== activePage) {
      window.history.pushState({ page: activePage }, '', window.location.href);
    }
    // Update Datadog view name to reflect the current page
    updateDatadogView(activePage);
  }, [activePage]);

  // Respond to browser back/forward by updating activePage from History state
  useEffect(() => {
    const onPopState = (e: PopStateEvent) => {
      const state = e.state as { page?: string } | null;
      if (state && typeof state.page === 'string') {
        const nextPage = state.page as string;
        if (nextPage && nextPage !== activePage) {
          const allowed = requestPageChange(nextPage);
          if (!allowed) {
            window.history.pushState(
              { page: activePage },
              '',
              window.location.href
            );
          }
        }
      } else {
        // If there's no state, re-push the current page to avoid accidental exits
        if (typeof window !== 'undefined') {
          const current =
            (window.history.state as { page?: string } | null)?.page ||
            activePage;
          if (current) {
            window.history.pushState(
              { page: current },
              '',
              window.location.href
            );
          }
        }
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [activePage, requestPageChange]);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        login,
        logout,
        isLoading,
        user,
        setUser,
        updateUserInContext,
        ssoLogin: initiateSSOLogin,
        activePage,
        setActivePage: (page: string) => {
          requestPageChange(page);
        },
        userOrgs,
        organization,
        setOrganization,
        changeOrganization,
        refreshUserOrganizations: fetchAndSetOrganizations,
        setMappingSessionKeepAlive,
      }}
    >
      {isLoading ? (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#fff',
            // Stay below the global Toaster (see index.tsx) so error toasts during
            // initializeApp/login remain clickable.
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {children}
          {isShowingWarning && (
            <IdleLogout
              isShowingWarning={isShowingWarning}
              setIsShowingWarning={setIsShowingWarning}
              updateIdleExpiryTime={() => extendIdleSession(true)}
              onLogout={logout}
            />
          )}
        </>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
